import React, { useState, useCallback, useRef } from 'react';
import {
	Box,
	Button,
	Modal,
	SpaceBetween,
	FormField,
	Input,
	Checkbox,
	Alert,
	KeyValuePairs,
} from '@amzn/awsui-components-console';

import { callSNOWDataService } from 'src/utils/snowDataServiceWrapper';
import { useAuthContext } from 'src/providers/AuthContext';
import { logger } from 'src/utils/logger';
import { SDS_APIGATEWAY_ENDPOINT } from 'src/config';

interface CopyToNewProps {
	sourceEntityName: string;
	workflowType: string;
	workflowName?: string;
	onSuccess?: (destinationName: string) => void;
}

export default function CopyToNew({ sourceEntityName, workflowType, workflowName, onSuccess }: CopyToNewProps) {
	const [modalVisible, setModalVisible] = useState(false);
	const [destinationName, setDestinationName] = useState('');
	const [isCopying, setIsCopying] = useState(false);
	const [validationError, setValidationError] = useState('');
	const [apiError, setApiError] = useState('');
	const [acknowledged, setAcknowledged] = useState(false);
	const isSubmitting = useRef(false);
	const { userName } = useAuthContext();

	const handleOpenModal = () => {
		setModalVisible(true);
	};

	const handleDismiss = () => {
		setDestinationName('');
		setValidationError('');
		setApiError('');
		setAcknowledged(false);
		setModalVisible(false);
	};

	const handleNameChange = (value: string) => {
		setDestinationName(value);

		if (value && !/^[a-zA-Z0-9]+$/.test(value)) {
			setValidationError('Invalid format: only alphanumeric characters allowed');
		} else {
			setValidationError('');
		}

		if (apiError) {
			setApiError('');
		}
	};

	const handleConfirm = useCallback(async () => {
		if (!destinationName.trim() || !userName || isSubmitting.current) return;

		if (!/^[a-zA-Z0-9]+$/.test(destinationName.trim())) {
			setValidationError('Invalid format: only alphanumeric characters allowed');

			return;
		}

		isSubmitting.current = true;
		setIsCopying(true);
		setApiError('');

		try {
			await callSNOWDataService({
				path: '/entity/copy',
				method: 'POST',
				url: SDS_APIGATEWAY_ENDPOINT,
				body: {
					sourceEntityName,
					sourceWorkflowType: workflowType,
					destinationEntityName: destinationName.trim(),
					requestedBy: userName,
				},
			});

			logger.info(
				`Successfully created draft entity ${destinationName} from source ${sourceEntityName}`
			);

			setModalVisible(false);
			setDestinationName('');
			setAcknowledged(false);

			if (onSuccess) onSuccess(destinationName.trim());
		} catch (error) {
			const errMsg = error instanceof Error ? error.message : String(error);

			logger.error(`Failed to copy entity: ${errMsg}`);
			setApiError(
				errMsg.includes('already exists')
					? 'Entity already exists. Please choose a different name.'
					: `Copy failed: ${errMsg}`
			);
		} finally {
			setIsCopying(false);
			isSubmitting.current = false;
		}
	}, [destinationName, sourceEntityName, workflowType, userName, onSuccess]);

	return (
		<>
			<Button
				variant="normal"
				onClick={handleOpenModal}
				data-testid="copy-to-new--button"
			>
				Copy to New
			</Button>

			<Modal
				visible={modalVisible}
				onDismiss={handleDismiss}
				header="Copy to New Entity"
				closeAriaLabel="Close modal"
				size="medium"
				footer={
					<Box>
						<SpaceBetween direction="vertical" size="s">
							<Checkbox
								checked={acknowledged}
								onChange={({ detail }) => setAcknowledged(detail.checked)}
								data-testid="copy-to-new--acknowledge"
							>
								I confirm I want to create a new entity from this source
							</Checkbox>
							<Box float="right">
								<SpaceBetween direction="horizontal" size="xs">
									<Button
										variant="link"
										onClick={handleDismiss}
										disabled={isCopying}
									>
										Cancel
									</Button>
									<Button
										variant="primary"
										onClick={handleConfirm}
										loading={isCopying}
										disabled={!acknowledged || !destinationName.trim() || !!validationError}
										data-testid="copy-to-new--confirm"
									>
										Create Copy
									</Button>
								</SpaceBetween>
							</Box>
						</SpaceBetween>
					</Box>
				}
			>
				<SpaceBetween size="m">
					{apiError && (
						<Alert type="error" dismissible onDismiss={() => setApiError('')}>
							{apiError}
						</Alert>
					)}

					<Alert type="info">
						A new draft entity will be created by copying the configuration from the source below.
						Please provide a name for the new entity.
					</Alert>

					<KeyValuePairs
						columns={2}
						items={[
							{
								label: 'Source Entity',
								value: sourceEntityName,
							},
							{
								label: 'Workflow',
								value: workflowName || workflowType,
							},
						]}
					/>

					<FormField
						label="New Entity Name"
						description="Enter the name for the new entity (alphanumeric characters only)"
						errorText={validationError}
						data-testid="copy-to-new--name-field"
					>
						<Input
							value={destinationName}
							onChange={({ detail }) => handleNameChange(detail.value)}
							placeholder="e.g. AFT10"
							disabled={isCopying}
							data-testid="copy-to-new--input"
						/>
					</FormField>
				</SpaceBetween>
			</Modal>
		</>
	);
}
